import React, { useEffect, useMemo, useState } from 'react';
import Swal from '../../Librarian/librarianSwal';
import Loader from '../../Layout/Loader';
import useMinLoading from '../../Layout/useMinLoading';
import SearchableSelect from '../../shared/SearchableSelect';
import {
  getAllLibrariesAdmin,
  getLibrarianAuthList,
  addLibraryAdmin,
  updateLibraryAdmin,
  deleteLibraryAdmin,
  getErrorMessage
} from '../../api/api';
import '../../Librarian/lib-page-header.css';
import '../../Librarian/BookList.css';

const EMPTY_FORM = { library_name: '', library_code: '', employee_authorization_list_id: '' };

const SchoolList = () => {
  const [loading, setLoading] = useState(false);
  const showLoader = useMinLoading(loading);

  const [libraries, setLibraries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = add, object = edit
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [libs, emps] = await Promise.all([
        getAllLibrariesAdmin(),
        getLibrarianAuthList().catch(() => [])
      ]);
      setLibraries(Array.isArray(libs) ? libs : []);
      setEmployees(Array.isArray(emps) ? emps : []);
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return libraries;
    return libraries.filter(
      (l) =>
        String(l.library_name || '').toLowerCase().includes(q) ||
        String(l.library_code || '').toLowerCase().includes(q)
    );
  }, [libraries, search]);

  const empName = (id) => {
    const e = employees.find((x) => String(x.id) === String(id));
    if (!e) return '—';
    return `${e.first_name || ''} ${e.last_name || ''}`.trim() || `#${id}`;
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (lib) => {
    setEditing(lib);
    setForm({
      library_name: lib.library_name || '',
      library_code: lib.library_code || '',
      employee_authorization_list_id: lib.employee_id || lib.employee_authorization_list_id || ''
    });
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.library_name.trim() || !form.library_code.trim()) {
      Swal.fire('Missing fields', 'Library name and code are required.', 'warning');
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await updateLibraryAdmin(editing.id, form);
        Swal.fire('Updated', 'Library updated successfully.', 'success');
      } else {
        await addLibraryAdmin(form);
        Swal.fire('Added', 'Library added successfully.', 'success');
      }
      setModalOpen(false);
      fetchAll();
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lib) => {
    const res = await Swal.fire({
      title: 'Delete library?',
      text: `"${lib.library_name}" will be permanently removed.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626'
    });
    if (!res.isConfirmed) return;
    try {
      await deleteLibraryAdmin(lib.id);
      setLibraries((prev) => prev.filter((l) => l.id !== lib.id));
      Swal.fire('Deleted', 'Library deleted.', 'success');
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header ms-0">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon"><i className="fas fa-school" /></div>
          <div>
            <h3 className="lib-page-header__title">Schools / Libraries</h3>
            <div className="lib-page-header__subtitle">Create and manage every library in the system</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button type="button" className="book-add-btn" onClick={openAdd}>
            <i className="fas fa-plus" /> Add School
          </button>
        </div>
      </div>

      <div className="book-filter-card">
        <div className="book-filter-row">
          <div className="search__input" style={{ position: 'relative', flex: 1 }}>
            <input
              type="text"
              className="book-search-input"
              placeholder="Search by name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <span style={{ color: '#6b7a99', fontSize: 13, fontWeight: 600 }}>
            {filtered.length} librar{filtered.length === 1 ? 'y' : 'ies'}
          </span>
        </div>
      </div>

      {showLoader ? (
        <Loader />
      ) : (
        <div className="book-table-card">
          <div className="book-table-wrapper">
            <table className="book-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Sr.</th>
                  <th>Library Name</th>
                  <th>Code</th>
                  <th>Authorized Employee</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="book-empty">No libraries found</td>
                  </tr>
                ) : (
                  filtered.map((lib, i) => (
                    <tr key={lib.id}>
                      <td>{i + 1}</td>
                      <td><strong>{lib.library_name}</strong></td>
                      <td><span className="book-count book-count-blue">{lib.library_code || '—'}</span></td>
                      <td>{empName(lib.employee_id || lib.employee_authorization_list_id)}</td>
                      <td>
                        <div className="book-action-buttons">
                          <button className="book-action-btn edit" onClick={() => openEdit(lib)}>Edit</button>
                          <button className="book-action-btn delete" onClick={() => handleDelete(lib)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="cat-modal-overlay" onClick={(e) => e.target === e.currentTarget && setModalOpen(false)}>
          <div className="cat-modal">
            <div className="cat-modal-header">
              <div>
                <h4>{editing ? 'Edit School' : 'Add School'}</h4>
                <p>{editing ? 'Update this library’s details' : 'Create a new library / school'}</p>
              </div>
              <button className="cat-modal-close" onClick={() => setModalOpen(false)}>
                <i className="ti ti-x" />
              </button>
            </div>

            <div className="cat-form">
              <div className="cat-form-row">
                <div className="cat-form-group">
                  <label>Library / School Name <span>*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. Central City Library"
                    value={form.library_name}
                    onChange={(e) => setForm({ ...form, library_name: e.target.value })}
                  />
                </div>
                <div className="cat-form-group">
                  <label>Library Code <span>*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. CCL-01"
                    value={form.library_code}
                    onChange={(e) => setForm({ ...form, library_code: e.target.value })}
                  />
                </div>
              </div>
              <div className="cat-form-row">
                <div className="cat-form-group">
                  <label>Authorized Employee</label>
                  <SearchableSelect
                    value={form.employee_authorization_list_id}
                    onChange={(e) => setForm({ ...form, employee_authorization_list_id: e.target.value })}
                    options={employees.map((emp) => ({
                      value: emp.id,
                      label: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || `#${emp.id}`
                    }))}
                    placeholder="Select employee"
                  />
                </div>
              </div>
              <div className="cat-form-actions">
                <button className="cat-btn-save" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : editing ? 'Update School' : 'Add School'}
                </button>
                <button className="cat-btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolList;
